export type MeetingType =
  | 'onboarding'
  | 'regular_checkin'
  | 'project_review'
  | 'career_guidance'
  | 'skill_assessment'
  | 'goal_setting'
  | 'feedback_session'
  | 'other'

export type MeetingPlatform =
  | 'zoom'
  | 'google_meet'
  | 'microsoft_teams'
  | 'skype'
  | 'in_person'
  | 'phone'
  | 'other'

export type MeetingRequestStatus = 'pending' | 'accepted' | 'rejected' | 'expired'

export interface ProposedTimeSlot {
  start: string
  end: string
}

export interface MeetingAgendaItem {
  id?: string
  title: string
  description?: string
  estimatedMinutes: number
  order: number
  isCompleted?: boolean
}

export interface MeetingActionItem {
  id?: string
  title: string
  description?: string
  assignedTo: 'mentor' | 'mentee'
  dueDate?: string
  isCompleted?: boolean
  completedAt?: string
}

export interface AddMeetingActionItemData {
  title: string
  assignedTo: 'mentor' | 'mentee'
  description?: string
  dueDate?: string
}

export type RecurrenceFrequency = 'weekly' | 'biweekly' | 'monthly'

export interface RecurringPattern {
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek?: number[]
  endDate?: string
  maxOccurrences?: number
}

export interface MeetingRequest {
  id: string
  requesterId: string
  recipientId: string
  proposedTimes: ProposedTimeSlot[]
  duration: number
  type: MeetingType
  platform: MeetingPlatform
  meetingUrl?: string
  message?: string
  agenda: MeetingAgendaItem[]
  actionItems: MeetingActionItem[]
  status: MeetingRequestStatus
  selectedTime?: ProposedTimeSlot
  isRecurring?: boolean
  recurringPattern?: RecurringPattern
  planId?: string
  createdAt: string
  updatedAt: string
  respondedAt?: string
}

export type MeetingStatus =
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'rescheduled'

export interface Meeting {
  id: string
  meetingRequestId: string
  mentorId: string
  menteeId: string
  createdBy: string
  status: MeetingStatus
  type: MeetingType
  platform: MeetingPlatform
  meetingUrl?: string
  duration: number
  scheduledStartTime: string
  scheduledEndTime: string
  agenda: MeetingAgendaItem[]
  actionItems: MeetingActionItem[]
  notes?: string
  isRecurring?: boolean
  parentMeetingId?: string
  planId?: string
  createdAt: string
  updatedAt: string
}

export interface AcceptMeetingRequestResponse {
  request: MeetingRequest
  meeting: Meeting
}

export interface RescheduleMeetingResponse {
  meeting: Meeting
  request: MeetingRequest
}

export interface CreateMeetingRequestData {
  recipientId: string
  proposedTimes: ProposedTimeSlot[]
  duration: number
  type: MeetingType
  platform: MeetingPlatform
  meetingUrl?: string
  message?: string
  agenda: MeetingAgendaItem[]
  actionItems: MeetingActionItem[]
  isRecurring?: boolean
  recurringPattern?: RecurringPattern
  planId?: string
}

export interface RecurringPatternValue {
  enabled: boolean
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek: number[]
  endMode: 'date' | 'occurrences'
  endDate: string
  maxOccurrences: number | ''
}

export const DEFAULT_RECURRING_PATTERN: RecurringPatternValue = {
  enabled: false,
  frequency: 'weekly',
  interval: 1,
  daysOfWeek: [],
  endMode: 'occurrences',
  endDate: '',
  maxOccurrences: 4
}
